import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, finalize, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';
import { FertilizerImportDialogComponent } from 'src/app/fertilizers/fertilizer-import-dialog/fertilizer-import-dialog.component';
import {
  AdvisedFertilizerResponse,
  FertilizerSource,
  MineralBalanceResponse,
} from 'src/app/fertilizers/models/fertilizer.model';
import * as FertilizersActions from 'src/app/fertilizers/store/actions';
import { ManureTabType } from 'src/app/manure-space/models/manure-space.models';
import * as AppActionTypes from 'src/app/store/actions';
import { getCropYear } from 'src/app/store/selectors';

import * as ManureSpaceActions from './manure-space.actions';

import { selectActiveTabIndex } from './manure-space.selectors';

import { ManureSpaceApiService } from '../services/manure-space-api.service';

@Injectable()
export class ManureSpaceEffects {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly api = inject(ManureSpaceApiService);
  protected readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  resetManureDataOnCropYearChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SetCropYear),
      map(() => ManureSpaceActions.resetManureData()),
    ),
  );

  getManureSpace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManureSpaceActions.getManureSpace),
      withLatestFrom(this.store.select(getCropYear)),
      switchMap(([_, cropYear]) => {
        if (!cropYear) return of();
        return this.api.getManureSpace(cropYear).pipe(
          map((manureSpace) => {
            return ManureSpaceActions.setManureSpaceSuccess({ manureSpace });
          }),
          catchError((error: HttpErrorResponse) => {
            // Handle 404 as valid response (no manure space exists yet)
            if (error.status === 404) {
              return of(ManureSpaceActions.setManureSpaceSuccess({ manureSpace: null }));
            }
            // Otherwise propagate the error
            return of(ManureSpaceActions.setManureSpaceFailure({ error }));
          }),
        );
      }),
    ),
  );

  getManureSpaceByManureCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManureSpaceActions.getManureSpaceByManureCode),
      withLatestFrom(this.store.select(getCropYear)),
      switchMap(([_, cropYear]) => {
        if (!cropYear) return of();
        return this.api.getManureSpaceByManureCode(cropYear).pipe(
          map((manureSpace) => {
            return ManureSpaceActions.setManureSpaceFilteredByManureCodeSuccess({ manureSpace });
          }),
          catchError((error: HttpErrorResponse) => {
            // Handle 404 as valid response (no manure space exists yet)
            if (error.status === 404) {
              return of(
                ManureSpaceActions.setManureSpaceFilteredByManureCodeSuccess({ manureSpace: null }),
              );
            }
            // Otherwise propagate the error
            return of(ManureSpaceActions.setManureSpaceFilteredByManureCodeFailure({ error }));
          }),
        );
      }),
    ),
  );

  getManureSpaceByPeriod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManureSpaceActions.getManureSpaceByPeriod),
      withLatestFrom(this.store.select(getCropYear)),
      switchMap(([_, cropYear]) => {
        if (!cropYear) return of();
        return this.api.getManureSpaceByPeriod(cropYear).pipe(
          map((manureSpace) => {
            return ManureSpaceActions.setManureSpaceByPeriodSuccess({ manureSpace });
          }),
          catchError((error: HttpErrorResponse) => {
            // Handle 404 as valid response (no manure space exists yet for this period)
            if (error.status === 404) {
              return of(ManureSpaceActions.setManureSpaceByPeriodSuccess({ manureSpace: null }));
            }
            // Otherwise propagate the error
            return of(ManureSpaceActions.setManureSpaceByPeriodFailure({ error }));
          }),
        );
      }),
    ),
  );

  getMineralBalance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManureSpaceActions.getMineralBalance),
      switchMap(({ importParams }) =>
        this.api.getMineralBalance(importParams.deliveriesFrom, importParams.deliveriesUntil).pipe(
          catchError((error: HttpErrorResponse) =>
            error.status === 404 ? of(null) : throwError(error),
          ),
          map((mineralBalance: MineralBalanceResponse) =>
            ManureSpaceActions.setFertilizersFromDelivery(mineralBalance),
          ),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
            this.store.dispatch(ManureSpaceActions.toggleMineralImport());
          }),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getAdvisedFertilizers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManureSpaceActions.getAdvisedFertilizers),
      switchMap(({ cropYear }) =>
        this.api.getAdvisedFertilizers(cropYear).pipe(
          catchError((error: HttpErrorResponse) =>
            error.status === 404 ? of(null) : throwError(error),
          ),
          map((advisedFertilizers: AdvisedFertilizerResponse) =>
            ManureSpaceActions.setAdvisedFertilizers(advisedFertilizers),
          ),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
            this.store.dispatch(ManureSpaceActions.toggleAdvisedFertilizersImport());
          }),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  openFertilizerImportConfirmationDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ManureSpaceActions.toggleMineralImport,
        ManureSpaceActions.toggleAdvisedFertilizersImport,
        ManureSpaceActions.toggleAnimalManureTransportImport,
        ManureSpaceActions.toggleSewageSludgeCompostTransportImport,
      ),
      switchMap((action) => {
        let dialogTitle: string;
        let fertilizerSource = FertilizerSource.Unknown;

        switch (action.type) {
          case ManureSpaceActions.toggleMineralImport.type:
            fertilizerSource = FertilizerSource.Delivery;
            dialogTitle = this.locale.translate(`importMineralBalance`);
            break;
          case ManureSpaceActions.toggleAdvisedFertilizersImport.type:
            fertilizerSource = FertilizerSource.Advice;
            dialogTitle = this.locale.translate(`importAdvisedFertilizers`);
            break;
          case ManureSpaceActions.toggleAnimalManureTransportImport.type:
            fertilizerSource = FertilizerSource.ExternalVDM;
            dialogTitle = this.locale.translate(`animalManureTransport.table.importVDM`);
            break;
          case ManureSpaceActions.toggleSewageSludgeCompostTransportImport.type:
            fertilizerSource = FertilizerSource.ExternalVZC;
            dialogTitle = this.locale.translate(`sewageSludgeAndCompost.title`);
            break;

          default:
            break;
        }

        return this.dialog
          .dialog(FertilizerImportDialogComponent, {
            title: dialogTitle,
            confirmButton: true,
            isBatchEdit: true,
            fertilizerSource,
          })
          .pipe(switchMap(() => of(AppActionTypes.CloseImportDialog())));
      }),
    ),
  );

  handleFertilizerUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FertilizersActions.fertilizersUpdated),
      withLatestFrom(this.store.select(selectActiveTabIndex)),
      switchMap(([_, activeTab]) => {
        const actions = [];
        actions.push(ManureSpaceActions.resetManureData());
        switch (activeTab) {
          case ManureTabType.ManureDetails:
            actions.push(ManureSpaceActions.getManureSpace());
            break;
          case ManureTabType.ManureType:
            actions.push(ManureSpaceActions.getManureSpaceByManureCode());
            break;
          case ManureTabType.ManurePeriod:
            actions.push(ManureSpaceActions.getManureSpaceByPeriod());
            break;
        }
        return actions;
      }),
    ),
  );

  updateDataAfterCropYearChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.CropYearChanged),
      withLatestFrom(this.store.select(selectActiveTabIndex)),
      switchMap(([_, activeTab]) => {
        const actions = [];
        actions.push(ManureSpaceActions.resetManureData());
        switch (activeTab) {
          case ManureTabType.ManureDetails:
            actions.push(ManureSpaceActions.getManureSpace());
            break;
          case ManureTabType.ManureType:
            actions.push(ManureSpaceActions.getManureSpaceByManureCode());
            break;
          case ManureTabType.ManurePeriod:
            actions.push(ManureSpaceActions.getManureSpaceByPeriod());
            break;
        }
        return actions;
      }),
    ),
  );
}
